// Traccar WKT area strings use "lat lon" ordering (not standard WKT "lon lat").
interface CircleArea {
  type: 'circle';
  centerLat: number;
  centerLon: number;
  radius: number; // meters
}

interface PolygonArea {
  type: 'polygon';
  points: { lat: number; lon: number }[];
}

type ParsedGeofenceArea = CircleArea | PolygonArea;

const EARTH_RADIUS_METERS = 6371000;

const toRadians = (deg: number) => (deg * Math.PI) / 180;

const haversineDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return EARTH_RADIUS_METERS * c;
};

export const parseGeofenceArea = (area: string): ParsedGeofenceArea | null => {
  if (!area) return null;

  const circleMatch = area.match(/CIRCLE\s*\(\s*([-\d.]+)\s+([-\d.]+)\s*,\s*([-\d.]+)\s*\)/i);
  if (circleMatch) {
    return {
      type: 'circle',
      centerLat: parseFloat(circleMatch[1]),
      centerLon: parseFloat(circleMatch[2]),
      radius: parseFloat(circleMatch[3]),
    };
  }

  const polygonMatch = area.match(/POLYGON\s*\(\((.*)\)\)/i);
  if (polygonMatch) {
    const points = polygonMatch[1]
      .split(',')
      .map((pair) => {
        const [lat, lon] = pair.trim().split(/\s+/).map(parseFloat);
        return { lat, lon };
      })
      .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lon));
    return { type: 'polygon', points };
  }

  return null;
};

// Ray-casting algorithm; consistent lat/lon ordering makes the axis choice irrelevant.
const isPointInPolygon = (lat: number, lon: number, points: { lat: number; lon: number }[]): boolean => {
  let inside = false;
  for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
    const { lat: latI, lon: lonI } = points[i];
    const { lat: latJ, lon: lonJ } = points[j];
    const intersects =
      latI > lat !== latJ > lat &&
      lon < ((lonJ - lonI) * (lat - latI)) / (latJ - latI) + lonI;
    if (intersects) inside = !inside;
  }
  return inside;
};

export const isPointInGeofence = (lat: number, lon: number, area: string): boolean => {
  const parsed = parseGeofenceArea(area);
  if (!parsed) return false;

  if (parsed.type === 'circle') {
    return haversineDistance(lat, lon, parsed.centerLat, parsed.centerLon) <= parsed.radius;
  }

  return parsed.points.length >= 3 && isPointInPolygon(lat, lon, parsed.points);
};
